import { Tooltip } from '@chakra-ui/react';
import classNames from 'classnames';
import { group, scaleBand, scaleLinear, scaleOrdinal } from 'd3';
import { useState } from 'react';

import { ReplotChartNames } from '../../constants';
import type { CommonPlotProps } from '../../types';
import {
  getMaxTextWidth,
  useChartTheme,
  useResizeObserver,
} from '../../utilities';
import tooltipStyles from '../../utilities/chart-tooltip.module.css';
import { currencyFormatter } from '../../utilities/get-formatter';
import { plotTestId } from '../../utilities/plotTestId';
import { AxisLabel } from '../axis/axis-label';
import { PlotLoadWrapper } from '../plot-loader/plot-loader';
import styles from './box-plot.module.css';
import { TooltipContent } from './tooltip-content';

type BoxDatum = {
  percentile10: number;
  percentile25?: number;
  percentile50: number;
  percentile75?: number;
  percentile90: number;
};

export type BoxGroup = {
  dimension: string;
  boxData: BoxDatum;
  secondaryDimension?: string;
  color?: string;
};

export interface BoxPlotProps extends CommonPlotProps {
  data: BoxGroup[];
  colorMap?: Record<string, string>;
  colorPalette?: string[];
  showQuartiles?: boolean;
}

const Y_AXIS_MAX_WIDTH = 80;
const Y_AXIS_MAX_WIDTH_SMALL = 50;
const X_AXIS_HEIGHT = 20;
const PLOT_LEFT_PADDING = 8;
const PLOT_RIGHT_PADDING = 15;
const NEW_THEME_MAX_BOX_THICKNESS = 7;

export const BoxPlot = ({
  data,
  colorMap,
  colorPalette: colorPaletteProp,
  showQuartiles = false,
  loading = false,
  currencyCode,
}: BoxPlotProps) => {
  const { containerRef, width, height } = useResizeObserver();
  const { palette, isNewTheme } = useChartTheme();
  const colorPalette = colorPaletteProp ?? palette;

  /**
   * Deduplicate dimension labels so that multiple box groups with the same
   * dimension are treated independently. When secondaryDimension is absent,
   * use dimension as the display key (one box per row).
   */
  type DisplayBoxGroup = BoxGroup & {
    displaySecondaryDimension: string;
    _colorKey: string;
  };

  const displayData: DisplayBoxGroup[] = (() => {
    const secondaryKey = (d: BoxGroup) => d.secondaryDimension ?? d.dimension;
    const perDimensionCounts = new Map<string, Map<string, number>>();

    return data.map((d) => {
      const key = secondaryKey(d);
      const dimCounts =
        perDimensionCounts.get(d.dimension) ?? new Map<string, number>();
      const count = dimCounts.get(key) ?? 0;
      dimCounts.set(key, count + 1);
      perDimensionCounts.set(d.dimension, dimCounts);

      const displaySecondaryDimension =
        count === 0 ? key : `${key} (${count + 1})`;

      return {
        ...d,
        displaySecondaryDimension,
        _colorKey: displaySecondaryDimension,
      };
    });
  })();

  const isSmallPlot = width < 250;
  const yAxisMaxWidth = isSmallPlot ? Y_AXIS_MAX_WIDTH_SMALL : Y_AXIS_MAX_WIDTH;

  const plotHeight = height - X_AXIS_HEIGHT;

  /** ======== Colors ======== */
  const colorKeys = Array.from(new Set(displayData.map((d) => d._colorKey)));

  const colorScale = scaleOrdinal<string>()
    .domain(colorKeys)
    // @ts-ignore noUncheckedIndexedAccess
    .range(colorKeys.map((d, i) => colorMap?.[d] || colorPalette[i]));

  const getBoxColor = (d: DisplayBoxGroup): string =>
    d.color ?? colorScale(d._colorKey);

  /** ======== Data ======== */
  const groupedData = group(displayData, (d) => d.dimension);

  /** ======== Y Scale ======== */
  const yScale = scaleBand<string>(
    Array.from(new Set(displayData.map((d) => d.dimension))),
    [0, plotHeight]
  )
    .paddingOuter(0.1)
    .paddingInner(0.2);

  const getRowSecondaryScale = (values: DisplayBoxGroup[]) =>
    scaleBand<string>(
      values.map((d) => d.displaySecondaryDimension),
      [0, yScale.bandwidth()]
    ).paddingInner(values.length > 1 ? 0.2 : 0);

  const yTicks = yScale.domain();
  const yAxisWidth = Math.min(
    getMaxTextWidth({ texts: yTicks }),
    yAxisMaxWidth
  );

  /** ======== X Scale ======== */
  const plotWidth = width - yAxisWidth - PLOT_LEFT_PADDING - PLOT_RIGHT_PADDING;

  const minValue = Math.min(
    ...(displayData.flatMap((group) => group.boxData.percentile10) || [])
  );
  const maxValue = Math.max(
    ...(displayData.flatMap((group) => group.boxData.percentile90) || [])
  );
  const xScale = scaleLinear([minValue, maxValue], [0, plotWidth]);

  const xAxisTicks = xScale.ticks(5).map((tick) => ({
    value: tick,
    label: currencyFormatter('$~s', currencyCode)(tick),
  }));

  /** ======== Hover Behavior ======== */
  const [hoveredSecondaryDimension, setHoveredSecondaryDimension] =
    useState<string>();
  const onMouseOver = (secondaryDimension: string) => {
    setHoveredSecondaryDimension(secondaryDimension);
  };
  const onMouseOut = () => {
    setHoveredSecondaryDimension(undefined);
  };

  const isPlotSizeValid = plotWidth > 0 && plotHeight > 0;

  return (
    <div
      ref={containerRef}
      className={styles['container']}
      data-testid={plotTestId(ReplotChartNames.BoxPlot)}
    >
      <PlotLoadWrapper loading={loading} noData={data.length === 0}>
        <svg width="100%" height="100%">
          <g data-testid="y-axis">
            {isPlotSizeValid &&
              yTicks.map((domain) => {
                const y = yScale(domain);
                return (
                  y && (
                    <AxisLabel
                      key={domain}
                      y={y + yScale.bandwidth() / 2}
                      width={yAxisWidth}
                      label={domain}
                      availableHeight={yScale.bandwidth()}
                    />
                  )
                );
              })}
          </g>
          <g
            data-testid="x-axis"
            transform={`translate(${yAxisWidth + PLOT_LEFT_PADDING}, ${plotHeight})`}
            className={styles['xAxis']}
          >
            {isPlotSizeValid &&
              xAxisTicks.map(({ value, label }, i) => (
                <g key={i} transform={`translate(${xScale(value)}, 0)`}>
                  <text y="9" dy="0.71em">
                    {label}
                  </text>
                </g>
              ))}
          </g>
          <g
            id="chart"
            transform={`translate(${yAxisWidth + PLOT_LEFT_PADDING}, 0)`}
          >
            <g id="axis-grid">
              {isPlotSizeValid &&
                xAxisTicks.map((tick, i) => (
                  <line
                    key={i}
                    x1={xScale(tick.value)}
                    y1={0}
                    x2={xScale(tick.value)}
                    y2={plotHeight}
                    className={styles['axisGridLine']}
                  />
                ))}
            </g>
            <g id="plot">
              {isPlotSizeValid &&
                Array.from(groupedData)?.map(([dimension, values], i) => {
                  const rowSecondaryScale = getRowSecondaryScale(values);
                  return (
                    <g
                      key={i}
                      id="bar-group"
                      transform={`translate(0, ${yScale(dimension)})`}
                    >
                      {values.map((d, j) => {
                        const bandHeight = rowSecondaryScale.bandwidth();
                        const boxHeight = isNewTheme
                          ? Math.min(bandHeight, NEW_THEME_MAX_BOX_THICKNESS)
                          : bandHeight;
                        const boxY = isNewTheme
                          ? Math.round((bandHeight - boxHeight) / 2)
                          : 0;

                        return (
                          <Tooltip
                            key={j}
                            hasArrow
                            label={
                              <TooltipContent
                                title={d.displaySecondaryDimension}
                                subtitle={d.dimension}
                                rows={[
                                  {
                                    label: '10th percentile',
                                    value: currencyFormatter(
                                      '$.4s',
                                      currencyCode
                                    )(d.boxData.percentile10),
                                  },
                                  ...(showQuartiles &&
                                  d.boxData.percentile25 != null
                                    ? [
                                        {
                                          label: '25th percentile',
                                          value: currencyFormatter(
                                            '$.4s',
                                            currencyCode
                                          )(d.boxData.percentile25),
                                        },
                                      ]
                                    : []),
                                  {
                                    label: '50th percentile',
                                    value: currencyFormatter(
                                      '$.4s',
                                      currencyCode
                                    )(d.boxData.percentile50),
                                  },
                                  ...(showQuartiles &&
                                  d.boxData.percentile75 != null
                                    ? [
                                        {
                                          label: '75th percentile',
                                          value: currencyFormatter(
                                            '$.4s',
                                            currencyCode
                                          )(d.boxData.percentile75),
                                        },
                                      ]
                                    : []),
                                  {
                                    label: '90th percentile',
                                    value: currencyFormatter(
                                      '$.4s',
                                      currencyCode
                                    )(d.boxData.percentile90),
                                  },
                                ]}
                              />
                            }
                            padding="8px 12px"
                            placement="top"
                            borderRadius="4px"
                            data-testid="box-plot__tooltip"
                            className={tooltipStyles.surface}
                          >
                            <g
                              transform={`translate(0, ${rowSecondaryScale(d.displaySecondaryDimension)})`}
                              onMouseOver={() =>
                                onMouseOver(d.displaySecondaryDimension)
                              }
                              onMouseOut={() => onMouseOut()}
                              className={classNames({
                                // @ts-ignore noUncheckedIndexedAccess
                                [styles['dimmed']]:
                                  hoveredSecondaryDimension &&
                                  hoveredSecondaryDimension !==
                                    d.displaySecondaryDimension,
                              })}
                            >
                              <rect
                                x={xScale(d.boxData.percentile10)}
                                y={boxY}
                                width={
                                  xScale(d.boxData.percentile90) -
                                  xScale(d.boxData.percentile10)
                                }
                                height={boxHeight}
                                stroke={getBoxColor(d)}
                                fill={getBoxColor(d)}
                                className={styles['box']}
                                rx={isNewTheme ? 0 : 1}
                                ry={isNewTheme ? 0 : 1}
                                data-testid="box-plot__box"
                              />
                              {showQuartiles &&
                                d.boxData.percentile25 != null &&
                                d.boxData.percentile75 != null && (
                                  <>
                                    <rect
                                      x={xScale(d.boxData.percentile25)}
                                      y={boxY}
                                      width={
                                        xScale(d.boxData.percentile75) -
                                        xScale(d.boxData.percentile25)
                                      }
                                      height={boxHeight}
                                      fill={getBoxColor(d)}
                                      className={styles['quartileInnerBox']}
                                    />
                                    <line
                                      x1={xScale(d.boxData.percentile25)}
                                      y1={boxY}
                                      x2={xScale(d.boxData.percentile25)}
                                      y2={boxY + boxHeight}
                                      stroke={getBoxColor(d)}
                                      className={styles['quartileLine']}
                                    />
                                    <line
                                      x1={xScale(d.boxData.percentile75)}
                                      y1={boxY}
                                      x2={xScale(d.boxData.percentile75)}
                                      y2={boxY + boxHeight}
                                      stroke={getBoxColor(d)}
                                      className={styles['quartileLine']}
                                    />
                                  </>
                                )}
                              <line
                                x1={xScale(d.boxData.percentile50)}
                                y1={boxY}
                                x2={xScale(d.boxData.percentile50)}
                                y2={boxY + boxHeight}
                                stroke={getBoxColor(d)}
                                className={styles['boxMidLine']}
                              />
                            </g>
                          </Tooltip>
                        );
                      })}
                    </g>
                  );
                })}
            </g>
          </g>
        </svg>
      </PlotLoadWrapper>
    </div>
  );
};
